import {
  QUESTION_SUMMARY_MAX_INTERVIEW_COUNT,
  QUESTION_SUMMARY_MIN_INTERVIEW_COUNT,
} from "@repo/app-types/constants/shared";
import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Label } from "@repo/ui/Label/label";
import { type FC, useMemo } from "react";
import type { InterviewWithContactAndActivities } from "../../app/admin_client_types.js";
import {
  sentimentChartMargins,
  sentimentChartYDomain,
} from "../../helpers/chartHelpers.js";
import { useAppSelector } from "../../hooks/hook.js";
import { PaywallBanner } from "../billing/paywallBanner.js";
import { selectIsCompanyBlocked } from "../company/companySlice.js";
import { selectContactsDictionary } from "../contacts/contactsState.js";
import { selectInterviewsDictionary } from "../interviews/interviewsSlice.js";
import { selectKeywordTrackersForProject } from "../keywordTrackers/keywordTrackersSlice.js";
import { LineChartComponent } from "./charts/charts.js";
import { PlaceholderSentimentLineChart } from "./charts/placeholderSentimentLineChart.js";
import { selectSentimentTrendData } from "./insightsSlice.js";
import {
  InsightsCard,
  KeywordTrackerCardContainer,
} from "./keywordTracker/keywordTrackerCardContainer.js";
import { QuestionThemesTable } from "./questionThemesTable.js";
import { formatSentimentTickForChart } from "./sentimentScore.js";
import { SurveyInsightsSection } from "./surveyInsightsSection.js";

interface InsightsTabProps {
  project: ProjectWithInterviewCount;
  interviews: InterviewWithContactAndActivities[];
}

export const InsightsTab: FC<InsightsTabProps> = ({ project, interviews }) => {
  const keywordTrackers = useAppSelector(
    selectKeywordTrackersForProject(project),
  );
  const isCompanyBlocked = useAppSelector(selectIsCompanyBlocked);
  const sentimentTrendData = useAppSelector(selectSentimentTrendData);

  const contactsDictionary = useAppSelector(selectContactsDictionary);
  const interviewsDictionary = useAppSelector(selectInterviewsDictionary);

  const completedInterviewsCount = useMemo(
    () => interviews.filter((i) => i.status === "completed").length,
    [interviews],
  );

  // Question summary trackers are generated after at least 5 interviews have been completed
  const questionSummaryTrackers = keywordTrackers.filter(
    (t) => t.type === "question_summary",
  );
  const hasQuestionSummaryTrackers = questionSummaryTrackers.length > 0;

  const competitorTracker = keywordTrackers.find(
    (t) => t.type === "competitive",
  );

  if (isCompanyBlocked) return <PaywallBanner />;

  return (
    <div className="flex flex-col space-y-8">
      <div>
        <InsightsCard
          body={
            sentimentTrendData && sentimentTrendData.data.length > 1 ? (
              <div className="bg-white p-3">
                <LineChartComponent
                  data={sentimentTrendData.data}
                  height={200}
                  margin={sentimentChartMargins}
                  scale="time"
                  shouldShowTooltip
                  yDomain={sentimentChartYDomain}
                  yTickFormatter={formatSentimentTickForChart}
                />
              </div>
            ) : (
              <div className="bg-white p-3">
                <PlaceholderSentimentLineChart />
              </div>
            )
          }
          header={
            <div className="flex flex-row items-center space-x-1 text-base font-semibold text-foreground">
              Average sentiment over time
            </div>
          }
        />
      </div>
      <SurveyInsightsSection />
      {completedInterviewsCount > QUESTION_SUMMARY_MAX_INTERVIEW_COUNT && (
        <InfoBanner
          title={`Insights from the ${QUESTION_SUMMARY_MAX_INTERVIEW_COUNT} most recent interviews are shown below`}
        />
      )}
      <div>
        <Label className="mb-4 block" size={Size.LARGE}>
          Response insights
        </Label>
        {hasQuestionSummaryTrackers ? (
          <QuestionThemesTable
            keywordTrackers={questionSummaryTrackers}
            project={project}
          />
        ) : (
          <InfoBanner
            title={`After ${QUESTION_SUMMARY_MIN_INTERVIEW_COUNT} interviews have been completed, aggregate themes and insights for each of your interview questions will appear here.`}
          />
        )}
      </div>

      {competitorTracker ? (
        <KeywordTrackerCardContainer
          contactsDictionary={contactsDictionary}
          interviewCount={project.interview_count}
          interviewsDictionary={interviewsDictionary}
          key={`keyword-tracker-${competitorTracker.id}`}
          keywordTracker={competitorTracker}
          questionNumber={undefined}
        />
      ) : null}
    </div>
  );
};
